import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { api } from "@/api";
import { money } from "@/components/ui";
import { useSession } from "@/session/SessionProvider";

import type {
  CashierAdapter,
  CashierApplePayReadiness,
  CashierFlow,
  CashierLoadState,
  CashierMethodView,
  CashierSnapshot,
  CashierWalletView,
} from "./types";
import { useWalletBalance } from "./useWalletBalance";

type WalletBalance = Awaited<ReturnType<typeof api.balance>>;
type AdapterState = {
  key: string | null;
  snapshot: CashierSnapshot | null;
  error: boolean;
};

type CashierContextValue = {
  adapter: CashierAdapter | null;
  adapterSnapshot: CashierSnapshot | null;
  state: CashierLoadState;
  message: string | null;
  wallets: readonly CashierWalletView[];
  selectedWallet: CashierWalletView | null;
  totalBalance: string | null;
  depositMethods: readonly CashierMethodView[];
  withdrawMethods: readonly CashierMethodView[];
  applePay: CashierApplePayReadiness;
  isOpen: boolean;
  flow: CashierFlow;
  busy: boolean;
  openCashier: (flow?: CashierFlow, trigger?: HTMLElement | null) => void;
  closeCashier: () => void;
  setFlow: (flow: CashierFlow) => void;
  selectWallet: (walletId: string) => Promise<void>;
  refresh: () => Promise<void>;
};

const UNAVAILABLE_APPLE_PAY: CashierApplePayReadiness = {
  state: "unavailable",
  message: "Apple Pay is unavailable until the cashier adapter is connected.",
};

const CashierContext = createContext<CashierContextValue | null>(null);

function formatBalanceAtom(value: number | null, currency: string) {
  return value === null ? null : money(value, currency);
}

export function walletBalanceToView(
  balance: WalletBalance | null | undefined,
): CashierWalletView | null {
  if (
    !balance ||
    balance.walletFound === false ||
    balance.balanceCents === null ||
    !balance.currency
  ) {
    return null;
  }
  return {
    id: `wallet:${balance.currency}`,
    label: `${balance.currency} wallet balance`,
    currency: balance.currency,
    total: money(balance.balanceCents, balance.currency),
    cash: formatBalanceAtom(balance.cashBalanceCents, balance.currency),
    bonus: formatBalanceAtom(balance.bonusBalanceCents, balance.currency),
    locked: formatBalanceAtom(balance.lockedBalanceCents, balance.currency),
    iconUrl: null,
    sourceLabel: "Display-only wallet balance",
    readOnly: true,
    stale: balance.stale,
    asOf: balance.asOf,
  };
}

const FIAT_CURRENCIES = new Set([
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "NZD",
  "BRL",
  "MXN",
  "JPY",
  "INR",
]);

/** Fiat renders as currency; crypto as a trimmed 8dp amount. */
function formatWalletAmount(amount: string, currency: string): string {
  const parsed = Number.parseFloat(amount);
  if (!Number.isFinite(parsed)) return amount;
  if (FIAT_CURRENCIES.has(currency)) {
    return parsed.toLocaleString("en-US", { style: "currency", currency });
  }
  return parsed.toLocaleString("en-US", { maximumFractionDigits: 8 });
}

/**
 * Every PAM wallet (crypto included) as display rows: the primary currency
 * keeps its exact cents projection and leads; funded non-primary wallets
 * follow alphabetically. Display only — never an acceptance input.
 */
export function walletBalancesToViews(
  balance: WalletBalance | null | undefined,
): CashierWalletView[] {
  const primary = walletBalanceToView(balance);
  const rows = balance?.wallets ?? null;
  const views: CashierWalletView[] = primary ? [primary] : [];
  if (!balance || !rows?.length) return views;
  for (const row of rows) {
    const currency = row.currency.toUpperCase();
    if (primary && currency === primary.currency) continue;
    if (!row.hasBalance) continue;
    views.push({
      id: `wallet:${currency}`,
      label: `${currency} wallet balance`,
      currency,
      total: formatWalletAmount(row.amount, currency),
      cash: formatWalletAmount(row.cashAmount, currency),
      bonus: formatWalletAmount(row.bonusAmount, currency),
      locked: formatWalletAmount(row.lockedAmount, currency),
      iconUrl: null,
      sourceLabel: "Display-only wallet balance",
      readOnly: true,
      stale: balance.stale,
      asOf: balance.asOf,
    });
  }
  return views;
}

export function CashierProvider({
  adapter,
  children,
}: {
  adapter: CashierAdapter | null;
  children: ReactNode;
}) {
  const { session } = useSession();
  const adapterKey =
    adapter && session ? `${session.playerId}:${adapter.version}` : null;
  const [adapterState, setAdapterState] = useState<AdapterState>({
    key: null,
    snapshot: null,
    error: false,
  });
  const [selectedOverride, setSelectedOverride] = useState<{
    key: string;
    walletId: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [openState, setOpenState] = useState<{
    key: string | null;
    open: boolean;
  }>({ key: null, open: false });
  const [flow, setFlow] = useState<CashierFlow>("deposit");
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const adapterSnapshot =
    adapterState.key === adapterKey ? adapterState.snapshot : null;
  const adapterError =
    adapterState.key === adapterKey ? adapterState.error : false;
  const adapterLoading = Boolean(
    adapterKey &&
    (adapterState.key !== adapterKey ||
      (!adapterState.snapshot && !adapterState.error)),
  );
  const isOpen = Boolean(
    session && openState.open && openState.key === String(session.playerId),
  );

  const balance = useWalletBalance(session?.playerId);

  useEffect(() => {
    if (!adapter || !adapterKey) return;

    const controller = new AbortController();
    void adapter
      .load(controller.signal)
      .then(async (snapshot) => {
        if (controller.signal.aborted) return;
        setAdapterState({ key: adapterKey, snapshot, error: false });
        if (adapter.applePay && snapshot.applePay.state === "checking") {
          const readiness = await adapter.applePay.getReadiness(
            controller.signal,
          );
          if (controller.signal.aborted) return;
          setAdapterState((current) =>
            current.key === adapterKey && current.snapshot
              ? {
                  ...current,
                  snapshot: { ...current.snapshot, applePay: readiness },
                }
              : current,
          );
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setAdapterState({
            key: adapterKey,
            snapshot: null,
            error: true,
          });
        }
      });
    return () => controller.abort();
  }, [adapter, adapterKey]);

  const pamWallets = useMemo(
    () => walletBalancesToViews(balance.data),
    [balance.data],
  );
  const pamWallet = pamWallets[0] ?? null;
  const wallets = useMemo<readonly CashierWalletView[]>(() => {
    if (adapterSnapshot?.wallets.length) return adapterSnapshot.wallets;
    return pamWallets;
  }, [adapterSnapshot, pamWallets]);
  const selectedWallet = useMemo(() => {
    const id =
      (selectedOverride?.key === adapterKey
        ? selectedOverride.walletId
        : null) ??
      adapterSnapshot?.selectedWalletId ??
      pamWallet?.id ??
      null;
    return wallets.find((wallet) => wallet.id === id) ?? wallets[0] ?? null;
  }, [
    adapterKey,
    adapterSnapshot?.selectedWalletId,
    pamWallet?.id,
    selectedOverride,
    wallets,
  ]);
  const totalBalance = adapterSnapshot?.wallets.length
    ? adapterSnapshot.totalBalance
    : (pamWallet?.total ?? null);

  const state = useMemo<CashierLoadState>(() => {
    if (!session) return "unavailable";
    if (wallets.length) return "ready";
    if (balance.isPending || adapterLoading) return "loading";
    if (balance.isError || adapterError || adapterSnapshot?.state === "error") {
      return "error";
    }
    return adapterSnapshot?.state ?? "unavailable";
  }, [
    adapterError,
    adapterLoading,
    adapterSnapshot?.state,
    balance.isError,
    balance.isPending,
    session,
    wallets.length,
  ]);

  const message = useMemo(() => {
    if (!session) return "Sign in to view your balance.";
    if (state === "loading") return "Loading balance";
    if (state === "error") return "Balance unavailable";
    if (state === "unavailable") {
      return adapterSnapshot?.message ?? "Balance unavailable";
    }
    return null;
  }, [adapterSnapshot?.message, session, state]);

  const openCashier = useCallback(
    (nextFlow: CashierFlow = "deposit", trigger?: HTMLElement | null) => {
      returnFocusRef.current =
        trigger ??
        (document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null);
      setFlow(nextFlow);
      setOpenState({
        key: session ? String(session.playerId) : null,
        open: true,
      });
    },
    [session],
  );

  const closeCashier = useCallback(() => {
    setOpenState((current) => ({ ...current, open: false }));
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    if (target) window.requestAnimationFrame(() => target.focus());
  }, []);

  const selectWallet = useCallback(
    async (walletId: string) => {
      const wallet = wallets.find((item) => item.id === walletId);
      if (!adapter?.selectWallet || !wallet || wallet.readOnly) return;
      const controller = new AbortController();
      setBusy(true);
      try {
        const snapshot = await adapter.selectWallet(
          walletId,
          controller.signal,
        );
        if (snapshot && adapterKey) {
          setAdapterState({ key: adapterKey, snapshot, error: false });
        }
        if (adapterKey) {
          setSelectedOverride({ key: adapterKey, walletId });
        }
      } catch {
        if (adapterKey) {
          setAdapterState((current) => ({
            key: adapterKey,
            snapshot: current.key === adapterKey ? current.snapshot : null,
            error: true,
          }));
        }
      } finally {
        setBusy(false);
      }
    },
    [adapter, adapterKey, wallets],
  );

  const refresh = useCallback(async () => {
    const controller = new AbortController();
    const tasks: Promise<unknown>[] = [balance.refetch()];
    if (adapter?.refresh && adapterKey) {
      tasks.push(
        adapter.refresh(controller.signal).then((snapshot) => {
          setAdapterState({ key: adapterKey, snapshot, error: false });
        }),
      );
    }
    await Promise.allSettled(tasks);
  }, [adapter, adapterKey, balance]);

  const value = useMemo<CashierContextValue>(
    () => ({
      adapter,
      adapterSnapshot,
      state,
      message,
      wallets,
      selectedWallet,
      totalBalance,
      depositMethods: adapterSnapshot?.depositMethods ?? [],
      withdrawMethods: adapterSnapshot?.withdrawMethods ?? [],
      applePay: adapterSnapshot?.applePay ?? UNAVAILABLE_APPLE_PAY,
      isOpen,
      flow,
      busy,
      openCashier,
      closeCashier,
      setFlow,
      selectWallet,
      refresh,
    }),
    [
      adapter,
      adapterSnapshot,
      busy,
      closeCashier,
      flow,
      isOpen,
      message,
      openCashier,
      refresh,
      selectWallet,
      selectedWallet,
      state,
      totalBalance,
      wallets,
    ],
  );

  return (
    <CashierContext.Provider value={value}>{children}</CashierContext.Provider>
  );
}

export function useCashier() {
  const value = useContext(CashierContext);
  if (!value) {
    throw new Error("useCashier must be used inside CashierProvider");
  }
  return value;
}
